"use client"

import type React from "react"
import { useState, useEffect, useMemo, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Heart, ShoppingCart, Truck, Shield, RefreshCw, ChevronLeft, ChevronRight, Minus, Plus } from "lucide-react"
import type { RootState } from "../store/store"
import { addToCart } from "../store/slices/cartSlice"
import { addToWishlist, removeFromWishlist } from "../store/slices/wishlistSlice"
import ProductCard from "../components/common/ProductCard"
import LoadingSpinner from "../components/common/LoadingSpinner"
import LazyImage from "../components/common/LazyImage"
import SEOHead from "../components/common/SEOHead"
import toast from "react-hot-toast"
import Rating from "../components/products/Rating"
import CommentSection from "../components/products/CommentSection"
import ErrorBoundary from "../components/common/ErrorBoundary"
import { Helmet } from "react-helmet-async"
import { collection, getDocs, query, where } from "firebase/firestore"
import { db } from "../firebase/config"
import type { VariantColor, VariantSize, ProductVariant } from "../types/variants"
import { slugify, findVariantBySelection, getImagesForColor } from "../utils/variants"

// Helper to slugify strings for URLs - MUST match ProductCard exactly
function localSlugify(str: string): string {
  return slugify(str)
}

interface WardrobeVariant {
  id: string
  name: string
  price: number
  isActive: boolean
}

interface Product {
  id: string
  name: string
  slug?: string
  categories: string[]
  category: string
  price: number
  originalPrice?: number
  image: string
  images: string[]
  description: string
  features?: string[]
  stock: number
  rating: number
  averageRating?: number
  numRatings?: number
  totalRatings?: number
  reviews?: number
  tags?: string[]
  dimensions?: {
    length: number
    width: number
    height: number
  }
  material?: string
  warranty?: string
  isFeatured?: boolean
  isNewArrival?: boolean
  isBestSeller?: boolean
  createdAt?: string

  // Regular variants (optional)
  variants?: ProductVariant[]
  variantColors?: VariantColor[]
  variantSizes?: VariantSize[]

  wardrobeVariants?: WardrobeVariant[]
  enableWardrobeVariants?: boolean
  enableCustomSize?: boolean
  customSizeMultiplier?: number
}

const ProductDetailContent: React.FC = () => {
  // All hooks must be called at the top level, in the same order every time
  const params = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Extract params with defaults to ensure consistency
  const category = params.category as string
  const productName = params.productName as string

  // Redux selectors - always called
  const { user } = useSelector((state: RootState) => state.auth)
  const { items: wishlistItems = [] } = useSelector((state: RootState) => state.wishlist)

  // All useState hooks - always called in the same order
  const [product, setProduct] = useState<Product | null>(null)
  const [allProducts, setAllProducts] = useState<Product[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [selectedTab, setSelectedTab] = useState<"description" | "features" | "specifications">("description")
  const [pincode, setPincode] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedColor, setSelectedColor] = useState<string | null>(null)
  const [selectedSize, setSelectedSize] = useState<string | null>(null)

  const [selectedWardrobeVariant, setSelectedWardrobeVariant] = useState<string | null>(null)
  const [isCustomSize, setIsCustomSize] = useState(false)
  const [customDimensions, setCustomDimensions] = useState({
    height: "",
    width: "",
  })

  // useMemo hooks - always called in the same order
  const hasVariants = useMemo(() => {
    return Boolean(product?.variants && product.variants.length > 0)
  }, [product?.variants])

  const hasWardrobeVariants = useMemo(() => {
    return Boolean(product?.enableWardrobeVariants && product?.wardrobeVariants && product.wardrobeVariants.length > 0)
  }, [product?.enableWardrobeVariants, product?.wardrobeVariants])

  const selectedVariant = useMemo(() => {
    return findVariantBySelection(product?.variants, selectedSize, selectedColor)
  }, [product?.variants, selectedSize, selectedColor])

  const selectedWardrobeVariantData = useMemo(() => {
    if (!hasWardrobeVariants || !selectedWardrobeVariant) return null
    return product?.wardrobeVariants?.find((v) => v.id === selectedWardrobeVariant) || null
  }, [hasWardrobeVariants, selectedWardrobeVariant, product?.wardrobeVariants])

  const displayPrice = useMemo(() => {
    if (!product) return 0

    if (hasWardrobeVariants) {
      if (isCustomSize && product.enableCustomSize && product.customSizeMultiplier) {
        const height = Number.parseFloat(customDimensions.height) || 0
        const width = Number.parseFloat(customDimensions.width) || 0
        if (height > 0 && width > 0) {
          return height * width * product.customSizeMultiplier
        }
        return 0 // Show 0 until dimensions are entered
      } else if (selectedWardrobeVariantData) {
        return selectedWardrobeVariantData.price
      }
      // No wardrobe variant selected yet - show base price as placeholder
      return Number(product.price)
    }

    if (!hasVariants) {
      // No variants - use the base product price
      return Number(product.price)
    }
    // Has variants - show variant-specific price or base price if no variant selected
    if (selectedVariant) {
      const basePrice = Number(product.price)
      const modifier = Number(selectedVariant.priceModifier || 0)
      return basePrice + modifier
    }
    // No variant selected yet - show base price as placeholder
    return Number(product.price)
  }, [
    product,
    hasVariants,
    hasWardrobeVariants,
    selectedVariant,
    selectedWardrobeVariantData,
    isCustomSize,
    customDimensions,
    product?.enableCustomSize,
    product?.customSizeMultiplier,
  ])

  const originalPrice = useMemo(() => {
    if (!product) return null

    if (hasWardrobeVariants) {
      // For wardrobe variants, don't show original price unless product has one
      const origPrice = Number(product.originalPrice || 0)
      const currentPrice = Number(product.price)
      return origPrice > currentPrice ? origPrice : null
    }

    if (!hasVariants) {
      // No variants - show original price if it exists and is higher than current price
      const origPrice = Number(product.originalPrice || 0)
      const currentPrice = Number(product.price)
      return origPrice > currentPrice ? origPrice : null
    }
    // Has variants - show base price as original when variant is selected
    if (selectedVariant && Number(selectedVariant.priceModifier || 0) !== 0) {
      return Number(product.price)
    }
    // Show product's original price if it exists
    const origPrice = Number(product.originalPrice || 0)
    const currentPrice = Number(product.price)
    return origPrice > currentPrice ? origPrice : null
  }, [product, hasVariants, hasWardrobeVariants, selectedVariant])

  const variantImages = useMemo(() => {
    if (!product) return []
    const colorSpecificImages = getImagesForColor(product.images, product.variantColors, selectedColor)
    return colorSpecificImages
  }, [product, selectedColor])

  const productImages = useMemo(() => {
    const images = variantImages.length > 0 ? variantImages : product?.images || []
    const fallbackImages = product?.image ? [product.image] : []
    return images.length > 0 ? images : fallbackImages.filter(Boolean)
  }, [variantImages, product?.images, product?.image])

  const isInWishlist = useMemo(() => {
    return wishlistItems.some((item) => item.id === product?.id)
  }, [wishlistItems, product?.id])

  const similarProducts = useMemo(() => {
    if (!product) return []
    return allProducts
      .filter((p) => {
        if (p.id === product.id) return false
        const productCategories = product.categories || [product.category]
        const pCategories = p.categories || [p.category]
        return productCategories.some((cat) => pCategories.includes(cat))
      })
      .slice(0, 4)
  }, [allProducts, product])

  const selectedStock = useMemo(() => {
    if (hasWardrobeVariants) {
      // Wardrobe variants are typically made-to-order, so always in stock
      return 999
    }
    return hasVariants ? selectedVariant?.stock || 0 : product?.stock || 0
  }, [hasVariants, hasWardrobeVariants, selectedVariant?.stock, product?.stock])

  const colors = useMemo(() => product?.variantColors || [], [product?.variantColors])
  const sizes = useMemo(() => product?.variantSizes || [], [product?.variantSizes])

  const availableSizesForColor = useMemo(() => {
    if (!hasVariants || !product?.variants) return sizes.map((s) => s.label)
    if (!selectedColor) return sizes.map((s) => s.label)
    const available = new Set(
      product.variants
        .filter((v) => slugify(v.color) === slugify(selectedColor) && v.isActive !== false && (v.stock || 0) > 0)
        .map((v) => v.size),
    )
    const labels = sizes.map((s) => s.label)
    return labels.filter((l) => available.has(l) || labels.length === 0)
  }, [hasVariants, product?.variants, selectedColor, sizes])

  const availableColorsForSize = useMemo(() => {
    if (!hasVariants || !product?.variants) return colors.map((c) => c.name)
    if (!selectedSize) return colors.map((c) => c.name)
    const available = new Set(
      product.variants
        .filter((v) => slugify(v.size) === slugify(selectedSize) && v.isActive !== false && (v.stock || 0) > 0)
        .map((v) => v.color),
    )
    const names = colors.map((c) => c.name)
    return names.filter((n) => available.has(n) || names.length === 0)
  }, [hasVariants, product?.variants, selectedSize, colors])

  const structuredData = useMemo(() => {
    if (!product) return null
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.description,
      image: productImages,
      brand: {
        "@type": "Brand",
        name: "Anmol Furniture",
      },
      offers: {
        "@type": "Offer",
        price: displayPrice,
        priceCurrency: "INR",
        availability: selectedStock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        seller: {
          "@type": "Organization",
          name: "Anmol Furniture Bazaar",
        },
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: product.averageRating || product.rating,
        reviewCount: product.totalRatings || product.numRatings || 0,
      },
    }
  }, [product, productImages, displayPrice, selectedStock])

  // useCallback hooks for event handlers
  const loadProductData = useCallback(async () => {
    if (!category || !productName) {
      setError("Invalid product URL")
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const productsRef = collection(db, "products")
      const productsQuery = query(productsRef, where("isActive", "==", true))
      const productsSnapshot = await getDocs(productsQuery)
      const productsData: Product[] = []

      productsSnapshot.forEach((doc) => {
        const data = doc.data() as any

        let categories: string[] = []
        if (Array.isArray(data.categories)) {
          categories = data.categories.filter(Boolean)
        } else if (data.category) {
          categories = [data.category]
        } else {
          categories = ["furniture"]
        }

        const p: Product = {
          id: doc.id,
          ...data,
          categories: categories,
          category: categories[0] || "furniture",
          images: Array.isArray(data.images) ? data.images : data.image ? [data.image] : [],
          image: data.images?.[0] || data.image || data.thumbnail || "",
          averageRating: data.averageRating || data.rating || 0,
          totalRatings: data.totalRatings || data.numRatings || data.reviews || 0,
          reviews: data.totalRatings || data.numRatings || data.reviews || 0,
          tags: Array.isArray(data.tags) ? data.tags : [],
          variants: Array.isArray(data.variants) ? data.variants : undefined,
          variantColors: Array.isArray(data.variantColors) ? data.variantColors : undefined,
          variantSizes: Array.isArray(data.variantSizes) ? data.variantSizes : undefined,
          wardrobeVariants: Array.isArray(data.wardrobeVariants) ? data.wardrobeVariants : undefined,
          enableWardrobeVariants: Boolean(data.enableWardrobeVariants),
          enableCustomSize: Boolean(data.enableCustomSize),
          customSizeMultiplier: Number(data.customSizeMultiplier) || 1250,
        }
        productsData.push(p)
      })

      setAllProducts(productsData)

      const foundProduct = productsData.find((p) => {
        const productCategories = p.categories || [p.category]
        const matchesCategory = productCategories.some((pCat) => pCat && localSlugify(pCat) === category)
        const matchesName = localSlugify(p.name) === productName
        return matchesCategory && matchesName
      })

      if (foundProduct) {
        setProduct(foundProduct)
      } else {
        setError("Product not found")
      }
    } catch (error) {
      console.error("Error loading product:", error)
      setError("Failed to load product")
    } finally {
      setIsLoading(false)
    }
  }, [category, productName])

  const handleAddToCart = useCallback(() => {
    if (!user) {
      toast.error("Please login to add items to cart")
      navigate("/login")
      return
    }

    if (!product) return

    if (hasWardrobeVariants) {
      if (isCustomSize) {
        const height = Number.parseFloat(customDimensions.height) || 0
        const width = Number.parseFloat(customDimensions.width) || 0
        if (height <= 0 || width <= 0) {
          toast.error("Please enter valid custom dimensions")
          return
        }
      } else if (!selectedWardrobeVariant) {
        toast.error("Please select a wardrobe size")
        return
      }
    } else if (hasVariants) {
      if (!selectedSize || !selectedColor) {
        toast.error("Please select size and color")
        return
      }
      if (!selectedVariant) {
        toast.error("Selected combination is unavailable")
        return
      }
      if (selectedVariant.stock < quantity) {
        toast.error("Selected variant is out of stock for requested quantity")
        return
      }
    } else if (product.stock < quantity) {
      toast.error("The product is out of stock for requested quantity")
      return
    }

    const perUnitPrice = displayPrice
    const imagesForCart = productImages.length > 0 ? productImages : [product.image].filter(Boolean)
    const image = imagesForCart[0] || product.image

    for (let i = 0; i < quantity; i++) {
      dispatch(
        addToCart({
          id: product.id,
          name: product.name,
          price: perUnitPrice,
          basePrice: product.price,
          priceModifier: selectedVariant?.priceModifier || 0,
          image: image,
          images: imagesForCart,
          category: product.categories?.[0] || product.category,
          quantity: 1,
          variant: hasVariants
            ? {
                id: selectedVariant!.id,
                size: selectedSize!,
                color: selectedColor!,
                colorHex: selectedVariant!.colorHex,
                priceModifier: selectedVariant!.priceModifier || 0,
              }
            : undefined,
          wardrobeVariant: hasWardrobeVariants
            ? {
                id: selectedWardrobeVariant || "custom",
                name: isCustomSize
                  ? `Custom Size (${customDimensions.height}" × ${customDimensions.width}")`
                  : selectedWardrobeVariantData?.name || "",
                price: perUnitPrice,
                isCustomSize,
                customDimensions: isCustomSize
                  ? {
                      height: Number.parseFloat(customDimensions.height),
                      width: Number.parseFloat(customDimensions.width),
                    }
                  : undefined,
              }
            : undefined,
          size: hasVariants ? selectedSize! : undefined,
          color: hasVariants ? selectedColor! : undefined,
          colorHex: hasVariants ? selectedVariant?.colorHex : undefined,
        }),
      )
    }

    toast.success(`Added ${quantity} item(s) to cart!`)
  }, [
    user,
    product,
    hasVariants,
    hasWardrobeVariants,
    selectedSize,
    selectedColor,
    selectedVariant,
    selectedWardrobeVariant,
    selectedWardrobeVariantData,
    isCustomSize,
    customDimensions,
    quantity,
    displayPrice,
    productImages,
    navigate,
    dispatch,
  ])

  const handleBuyNow = useCallback(() => {
    if (!user) {
      toast.error("Please login to purchase items")
      navigate("/login")
      return
    }
    handleAddToCart()
    navigate("/checkout")
  }, [user, handleAddToCart, navigate])

  const handleWishlistToggle = useCallback(() => {
    if (!user) {
      toast.error("Please login to add items to wishlist")
      navigate("/login")
      return
    }

    if (!product) return

    if (isInWishlist) {
      dispatch(removeFromWishlist(product.id))
      toast.success("Removed from wishlist")
    } else {
      dispatch(
        addToWishlist({
          id: product.id,
          name: product.name,
          price: displayPrice,
          image: productImages?.[0] || product.image,
          category: product.categories?.[0] || product.category,
          variant: hasVariants
            ? {
                id: selectedVariant?.id,
                size: selectedSize || "",
                color: selectedColor || "",
                colorHex: selectedVariant?.colorHex,
              }
            : undefined,
          wardrobeVariant: hasWardrobeVariants
            ? {
                id: selectedWardrobeVariant || "custom",
                name: isCustomSize
                  ? `Custom Size (${customDimensions.height}" × ${customDimensions.width}")`
                  : selectedWardrobeVariantData?.name || "",
                price: displayPrice,
                isCustomSize,
                customDimensions: isCustomSize
                  ? {
                      height: Number.parseFloat(customDimensions.height),
                      width: Number.parseFloat(customDimensions.width),
                    }
                  : undefined,
              }
            : undefined,
        }),
      )
      toast.success("Added to wishlist!")
    }
  }, [
    user,
    product,
    isInWishlist,
    displayPrice,
    productImages,
    hasVariants,
    hasWardrobeVariants,
    selectedVariant,
    selectedSize,
    selectedColor,
    selectedWardrobeVariant,
    selectedWardrobeVariantData,
    isCustomSize,
    customDimensions,
    navigate,
    dispatch,
  ])

  const handleImageNavigation = useCallback(
    (direction: "prev" | "next") => {
      const images = productImages
      if (direction === "prev") {
        setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
      } else {
        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
      }
    },
    [productImages],
  )

  const handleQuantityChange = useCallback(
    (action: "increase" | "decrease") => {
      if (action === "increase" && quantity < selectedStock) {
        setQuantity((prev) => prev + 1)
      } else if (action === "decrease" && quantity > 1) {
        setQuantity((prev) => prev - 1)
      }
    },
    [quantity, selectedStock],
  )

  const checkPincode = useCallback(() => {
    if (!pincode) {
      toast.error("Please enter a pincode")
      return
    }
    if (pincode.length === 6) {
      toast.success("Delivery available in your area!")
    } else {
      toast.error("Please enter a valid 6-digit pincode")
    }
  }, [pincode])

  const handleRatingChange = useCallback(
    (newRating: number, newTotal: number) => {
      if (product) {
        setProduct({
          ...product,
          averageRating: newRating,
          totalRatings: newTotal,
        })
      }
    },
    [product],
  )

  // useEffect hooks - always called in the same order
  useEffect(() => {
    loadProductData()
  }, [loadProductData])

  useEffect(() => {
    // When product loads, auto-preselect a variant if only one choice or find first in-stock variant
    if (product && hasVariants) {
      const colors = product.variantColors || []
      const sizes = product.variantSizes || []

      if (!selectedColor && colors.length > 0) {
        setSelectedColor(colors[0].name)
      }
      if (!selectedSize && sizes.length > 0) {
        setSelectedSize(sizes[0].label)
      }

      // If still no valid selectedVariant, try to pick first in-stock
      const v = findVariantBySelection(product.variants, selectedSize, selectedColor)
      if (!v) {
        const firstInStock = product.variants?.find((vv) => vv.isActive !== false && (vv.stock || 0) > 0)
        if (firstInStock) {
          setSelectedColor(firstInStock.color)
          setSelectedSize(firstInStock.size)
        }
      }
    }

    if (product && hasWardrobeVariants && !selectedWardrobeVariant) {
      const activeVariants = product.wardrobeVariants?.filter((v) => v.isActive) || []
      if (activeVariants.length > 0) {
        setSelectedWardrobeVariant(activeVariants[0].id)
      }
    }
  }, [product?.id, hasVariants, hasWardrobeVariants, selectedSize, selectedColor, selectedWardrobeVariant, product])

  // Early returns after all hooks have been called
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading product details...</p>
        </div>
      </div>
    )
  }

  if (error || !product) {
    return (
      <>
        <SEOHead
          title="Product Not Found"
          description="The product you're looking for doesn't exist or has been removed."
        />
        <Helmet>
          <meta name="robots" content="noindex, follow" />
          <title>404 Not Found | Anmol Furniture Bazaar</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
            <p className="text-gray-600 mb-6">
              {error || "The product you're looking for doesn't exist or has been deleted."}
            </p>
            <div className="space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={() => navigate("/")}
                className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SEOHead
        title={`${product.name} - Premium ${product.categories?.[0] || product.category} | Anmol Furniture`}
        description={`${product.description.substring(0, 150)}... Buy now with free delivery and warranty.`}
        keywords={`${product.name}, ${product.categories?.join(", ") || product.category}, furniture, premium furniture, ${product.tags?.join(", ")}`}
        image={productImages[0]}
        type="product"
        structuredData={structuredData}
      />

      <main className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-4 sm:py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6 sm:mb-8" aria-label="Breadcrumb">
            <button onClick={() => navigate("/")} className="hover:text-primary-600">
              Home
            </button>
            <span>/</span>
            <button onClick={() => navigate(`/categories/${category}`)} className="hover:text-primary-600 capitalize">
              {category}
            </button>
            <span>/</span>
            <span className="text-gray-900 truncate">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 mb-12 sm:mb-16">
            {/* Product Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative aspect-square bg-white rounded-xl overflow-hidden shadow-lg">
                <LazyImage
                  src={productImages[currentImageIndex]}
                  alt={`${product.name} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover transition-opacity duration-300"
                  loading="eager"
                  width={600}
                  height={600}
                />

                {/* Image Navigation */}
                {productImages.length > 1 && (
                  <>
                    <button
                      onClick={() => handleImageNavigation("prev")}
                      className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button
                      onClick={() => handleImageNavigation("next")}
                      className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                      aria-label="Next image"
                    >
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </>
                )}

                {/* Wishlist Button */}
                <button
                  onClick={handleWishlistToggle}
                  className={`absolute top-3 sm:top-4 right-3 sm:right-4 p-2 sm:p-3 rounded-full transition-all duration-200 ${
                    isInWishlist ? "bg-red-500 text-white" : "bg-white text-gray-600 hover:bg-red-500 hover:text-white"
                  } shadow-lg hover:shadow-xl`}
                  aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
                >
                  <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${isInWishlist ? "fill-current" : ""}`} />
                </button>
              </div>

              {/* Thumbnail Images */}
              {productImages.length > 1 && (
                <div className="flex space-x-2 overflow-x-auto pb-2">
                  {productImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        currentImageIndex === index ? "border-primary-600" : "border-gray-200 hover:border-gray-300"
                      }`}
                      aria-label={`View image ${index + 1}`}
                    >
                      <LazyImage
                        src={image}
                        alt={`${product.name} thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                        width={80}
                        height={80}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-4 sm:space-y-6">
              {/* Title and Rating */}
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{product.name}</h1>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-4">
                  <Rating
                    productId={product.id}
                    averageRating={product.averageRating || product.rating}
                    totalRatings={product.totalRatings || product.numRatings || 0}
                    onRatingChange={handleRatingChange}
                  />
                </div>
              </div>

              {hasWardrobeVariants && (
                <div className="space-y-4">
                  {/* Size Selection */}
                  <div>
                    <div className="mb-3">
                      <span className="text-sm font-medium text-gray-700">Size Options</span>
                    </div>

                    {/* Custom Size Toggle */}
                    {product.enableCustomSize && (
                      <div className="mb-4">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={isCustomSize}
                            onChange={(e) => {
                              setIsCustomSize(e.target.checked)
                              if (e.target.checked) {
                                setSelectedWardrobeVariant(null)
                              }
                            }}
                            className="accent-primary-600"
                          />
                          Custom Size
                        </label>
                      </div>
                    )}

                    {isCustomSize ? (
                      <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-900">Enter Custom Dimensions</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Height (inches)</label>
                            <input
                              type="number"
                              value={customDimensions.height}
                              onChange={(e) => setCustomDimensions((prev) => ({ ...prev, height: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="24"
                              min="1"
                              step="0.1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Width (inches)</label>
                            <input
                              type="number"
                              value={customDimensions.width}
                              onChange={(e) => setCustomDimensions((prev) => ({ ...prev, width: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="36"
                              min="1"
                              step="0.1"
                            />
                          </div>
                        </div>
                        {customDimensions.height && customDimensions.width && (
                          <p className="text-sm text-gray-600">
                            Price calculation: {customDimensions.height}" × {customDimensions.width}" × ₹
                            {product.customSizeMultiplier} = ₹{displayPrice.toLocaleString()}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-2">
                        {product.wardrobeVariants
                          ?.filter((v) => v.isActive)
                          .map((variant) => (
                            <button
                              key={variant.id}
                              onClick={() => setSelectedWardrobeVariant(variant.id)}
                              className={`p-3 rounded-lg border text-left transition-all ${
                                selectedWardrobeVariant === variant.id
                                  ? "border-primary-600 bg-primary-50 text-primary-900"
                                  : "border-gray-300 bg-white hover:border-gray-400"
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-medium">{variant.name}</span>
                                <span className="text-sm font-semibold">₹{variant.price.toLocaleString()}</span>
                              </div>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Regular Variant Selectors */}
              {hasVariants && !hasWardrobeVariants && (
                <div className="space-y-4">
                  {/* Colors */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Color</span>
                      {selectedColor && <span className="text-xs text-gray-500">Selected: {selectedColor}</span>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {colors.map((c) => {
                        const isAvailable = availableColorsForSize.includes(c.name)
                        const isSelected = selectedColor && slugify(selectedColor) === slugify(c.name)
                        return (
                          <button
                            key={c.name}
                            onClick={() => {
                              if (!isAvailable) return
                              setSelectedColor(c.name)
                              setCurrentImageIndex(0)
                            }}
                            className={`relative w-9 h-9 rounded-full border-2 transition-all ${
                              isSelected ? "border-gray-900" : "border-gray-300"
                            } ${!isAvailable ? "opacity-40 cursor-not-allowed" : "hover:shadow"} `}
                            title={c.name}
                            aria-label={c.name}
                          >
                            <span
                              className="absolute inset-0 rounded-full"
                              style={{ backgroundColor: c.hex || "#ccc" }}
                            />
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Sizes */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Size</span>
                      {selectedSize && <span className="text-xs text-gray-500">Selected: {selectedSize}</span>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {sizes.map((s) => {
                        const isAvailable = availableSizesForColor.includes(s.label)
                        const isSelected = selectedSize && slugify(selectedSize) === slugify(s.label)
                        return (
                          <button
                            key={s.label}
                            onClick={() => {
                              if (!isAvailable) return
                              setSelectedSize(s.label)
                            }}
                            className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                              isSelected
                                ? "border-gray-900 bg-gray-900 text-white"
                                : "border-gray-300 bg-white text-gray-900"
                            } ${!isAvailable ? "opacity-40 cursor-not-allowed" : "hover:shadow"}`}
                          >
                            {s.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Price */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {hasWardrobeVariants && isCustomSize && (!customDimensions.height || !customDimensions.width)
                        ? "Enter dimensions"
                        : `₹${Math.round(displayPrice).toLocaleString("en-IN")}`}
                    </span>
                    {originalPrice && (
                      <span className="text-lg text-gray-500 line-through">
                        ₹{Math.round(originalPrice).toLocaleString("en-IN")}
                      </span>
                    )}
                    {originalPrice && originalPrice > displayPrice && (
                      <span className="text-sm bg-red-100 text-red-800 px-2 py-1 rounded">
                        {Math.round(((originalPrice - displayPrice) / originalPrice) * 100)}% OFF
                      </span>
                    )}
                  </div>
                  {hasWardrobeVariants && !isCustomSize && !selectedWardrobeVariant && (
                    <p className="text-sm text-gray-600">Select a size to see price</p>
                  )}
                  {hasWardrobeVariants && isCustomSize && (
                    <p className="text-sm text-gray-600">
                      Custom pricing: ₹{product.customSizeMultiplier} per square inch
                    </p>
                  )}
                  {hasVariants && !hasWardrobeVariants && !selectedVariant && (
                    <p className="text-sm text-gray-600">Select size and color to see final price</p>
                  )}
                  {hasVariants &&
                    !hasWardrobeVariants &&
                    selectedVariant &&
                    Number(selectedVariant.priceModifier || 0) !== 0 && (
                      <p className="text-sm text-gray-600">
                        Base price: ₹{Math.round(Number(product.price)).toLocaleString("en-IN")}
                        {Number(selectedVariant.priceModifier || 0) > 0 ? " + " : " - "}₹
                        {Math.abs(Math.round(Number(selectedVariant.priceModifier || 0))).toLocaleString("en-IN")} for
                        this variant
                      </p>
                    )}
                </div>
              </div>

              {/* Stock Status */}
              <div
                className={`text-base sm:text-lg font-medium ${selectedStock > 0 ? "text-green-600" : "text-red-600"}`}
              >
                {hasWardrobeVariants ? (
                  <span>✓ Made to Order (Available)</span>
                ) : selectedStock > 0 ? (
                  <span>✓ In Stock ({selectedStock} available)</span>
                ) : (
                  <span>✗ Out of Stock</span>
                )}
              </div>

              {/* Quantity Selector */}
              {!hasWardrobeVariants && (
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <span className="text-gray-700 font-medium">Quantity:</span>
                  <div className="flex items-center border border-gray-300 rounded-lg w-fit">
                    <button
                      onClick={() => handleQuantityChange("decrease")}
                      disabled={quantity <= 1}
                      className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Decrease quantity"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-4 py-2 font-medium min-w-[3rem] text-center">{quantity}</span>
                    <button
                      onClick={() => handleQuantityChange("increase")}
                      disabled={quantity >= selectedStock}
                      className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Increase quantity"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <button
                  onClick={handleAddToCart}
                  disabled={
                    selectedStock === 0 ||
                    (hasVariants && !hasWardrobeVariants && (!selectedVariant || !selectedColor || !selectedSize)) ||
                    (hasWardrobeVariants && !isCustomSize && !selectedWardrobeVariant) ||
                    (hasWardrobeVariants && isCustomSize && (!customDimensions.height || !customDimensions.width))
                  }
                  className="flex items-center justify-center space-x-2 bg-primary-600 text-white py-3 px-4 sm:px-6 rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium text-sm sm:text-base"
                  aria-label={`Add ${quantity} ${product.name} to cart`}
                >
                  <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Add to Cart</span>
                </button>

                <button
                  onClick={handleBuyNow}
                  disabled={
                    selectedStock === 0 ||
                    (hasVariants && !hasWardrobeVariants && (!selectedVariant || !selectedColor || !selectedSize)) ||
                    (hasWardrobeVariants && !isCustomSize && !selectedWardrobeVariant) ||
                    (hasWardrobeVariants && isCustomSize && (!customDimensions.height || !customDimensions.width))
                  }
                  className="bg-green-600 text-white py-3 px-4 sm:px-6 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium text-sm sm:text-base"
                  aria-label={`Buy ${product.name} now`}
                >
                  Buy Now
                </button>
              </div>

              {/* Feature icons */}
              <div className="grid grid-cols-3 gap-4 py-4 border-t border-gray-200">
                <div className="text-center">
                  <Truck className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 mx-auto mb-2" />
                  <p className="text-xs sm:text-sm text-gray-600">Free Delivery</p>
                </div>
                <div className="text-center">
                  <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 mx-auto mb-2" />
                  <p className="text-xs sm:text-sm text-gray-600">{product.warranty || "1 Year"} Warranty</p>
                </div>
                <div className="text-center">
                  <RefreshCw className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 mx-auto mb-2" />
                  <p className="text-xs sm:text-sm text-gray-600">Easy Returns</p>
                </div>
              </div>
            </div>
          </div>

          {/* Product Details Tabs */}
          <section
            className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8 mb-12 sm:mb-16"
            aria-label="Product Details"
          >
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-8 border-b border-gray-200 mb-6 sm:mb-8">
              {[
                { key: "description", label: "Description" },
                { key: "features", label: "Features" },
                { key: "specifications", label: "Specifications" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setSelectedTab(tab.key as any)}
                  className={`pb-4 font-medium transition-colors text-sm sm:text-base ${
                    selectedTab === tab.key
                      ? "text-primary-600 border-b-2 border-primary-600"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  aria-selected={selectedTab === tab.key}
                  role="tab"
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="prose max-w-none" role="tabpanel">
              {selectedTab === "description" && (
                <div>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base lg:text-lg">{product.description}</p>
                </div>
              )}

              {selectedTab === "features" && (
                <div>
                  <ul className="space-y-3">
                    {(product.features || []).map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></span>
                        <span className="text-gray-700 text-sm sm:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedTab === "specifications" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 text-sm sm:text-base">Product Details</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-gray-600">Categories:</span>
                        <span className="font-medium capitalize">
                          {product.categories?.join(", ") || product.category || "N/A"}
                        </span>
                      </div>
                      {product.material && (
                        <div className="flex justify-between text-sm sm:text-base">
                          <span className="text-gray-600">Material:</span>
                          <span className="font-medium">{product.material}</span>
                        </div>
                      )}
                      {product.warranty && (
                        <div className="flex justify-between text-sm sm:text-base">
                          <span className="text-gray-600">Warranty:</span>
                          <span className="font-medium">{product.warranty}</span>
                        </div>
                      )}
                      {hasWardrobeVariants && (
                        <>
                          <div className="flex justify-between text-sm sm:text-base">
                            <span className="text-gray-600">Type:</span>
                            <span className="font-medium">Custom Wardrobe</span>
                          </div>
                          {product.enableCustomSize && (
                            <div className="flex justify-between text-sm sm:text-base">
                              <span className="text-gray-600">Custom Size:</span>
                              <span className="font-medium">Available (₹{product.customSizeMultiplier}/sq inch)</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {product.dimensions && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 text-sm sm:text-base">Dimensions</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm sm:text-base">
                          <span className="text-gray-600">Length:</span>
                          <span className="font-medium">{product.dimensions.length} in</span>
                        </div>
                        <div className="flex justify-between text-sm sm:text-base">
                          <span className="text-gray-600">Width:</span>
                          <span className="font-medium">{product.dimensions.width} in</span>
                        </div>
                        <div className="flex justify-between text-sm sm:text-base">
                          <span className="text-gray-600">Height:</span>
                          <span className="font-medium">{product.dimensions.height} in</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Similar Products */}
          {similarProducts.length > 0 && (
            <section aria-label="Similar Products">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8">Similar Products</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {similarProducts.map((similarProduct) => (
                  <ProductCard key={similarProduct.id} product={similarProduct} />
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Comments Section */}
        <div className="border-t bg-gray-50 p-4 sm:p-8 mt-8">
          <CommentSection productId={product.id} />
        </div>
      </main>
    </>
  )
}

const ProductDetail: React.FC = () => (
  <ErrorBoundary>
    <ProductDetailContent />
  </ErrorBoundary>
)

export default ProductDetail
